import { v4 as uuidv4 } from 'uuid';
import ReconnectingWebSocket from 'reconnecting-websocket';

import { AspenSocketMsg, RxMessage } from 'types/aspen/socketMessages';
import { LogAspenEventParams } from './aspenLogger';

// Extract base log event types that support request/response pattern
type RequestLogEventType =
  | 'iot.web.activity.image'
  | 'iot.web.event.query'
  | 'iot.web.event.video'
  | 'iot.web.event.delete';

export interface RequestOptions {
  timeout?: number;
  logEventType?: RequestLogEventType;
}

interface PendingRequest<T = unknown> {
  resolve: (value: T) => void;
  reject: (error: Error) => void;
  timeout: NodeJS.Timeout;
  requestType: string;
  responseType: string;
  startTime: number;
  logEventType?: RequestLogEventType;
}

interface Logger {
  logAspenEvent: (event: LogAspenEventParams) => void;
}

// Helper function to create type-safe log events
const createLogEvent = (
  logEventType: RequestLogEventType,
  eventType: 'request' | 'response',
  payload: object,
): LogAspenEventParams => {
  if (logEventType === 'iot.web.activity.image') {
    return {
      log_id: `iot.web.activity.image.${eventType}` as const,
      payload,
    } as LogAspenEventParams;
  }
  if (logEventType === 'iot.web.event.query') {
    return {
      log_id: `iot.web.event.query.${eventType}` as const,
      payload,
    } as LogAspenEventParams;
  }
  if (logEventType === 'iot.web.event.delete') {
    return {
      log_id: `iot.web.event.delete.${eventType}` as const,
      payload,
    } as LogAspenEventParams;
  }
  return {
    log_id: `iot.web.event.video.${eventType}` as const,
    payload,
  } as LogAspenEventParams;
};

// Module state
let connection: ReconnectingWebSocket | null = null;
let logger: Logger | null = null;
const pendingRequests = new Map<string, PendingRequest<unknown>>();

export const initializeAspenSocketsRequest = (socketConnection: ReconnectingWebSocket, socketLogger?: Logger): void => {
  // Cleanup existing requests if reinitializing
  if (connection) {
    cleanup();
  }

  connection = socketConnection;
  logger = socketLogger || null;
};

export const sendRequest = <TResponse, TPayload = unknown>(
  type: string,
  responseType: string,
  payload: TPayload,
  options: RequestOptions = {},
): Promise<{ response: TResponse; requestId: string }> => {
  if (!connection) {
    throw new Error('AspenSocketsRequest not initialized. Call initializeAspenSocketsRequest first.');
  }

  const { timeout = 5000, logEventType } = options;

  return new Promise((resolve, reject) => {
    const id = uuidv4();
    const message: AspenSocketMsg = { id, type, payload } as AspenSocketMsg;

    // Setup timeout
    const timeoutHandle = setTimeout(() => {
      const pending = pendingRequests.get(id);
      if (pending) {
        pendingRequests.delete(id);
        connection?.removeEventListener('message', listener);

        // Log timeout
        if (pending.logEventType && logger) {
          logger.logAspenEvent(
            createLogEvent(pending.logEventType, 'response', {
              request_id: id,
              success: false,
              error: 'timeout',
            }),
          );
        }

        const error = new Error(`Request timeout: ${type}`) as Error & { requestId?: string };
        error.requestId = id;
        reject(error);
      }
    }, timeout);

    // Create listener for this specific request
    const listener = (event: MessageEvent) => {
      const response: RxMessage = JSON.parse(event.data);
      if (response.id === id && response.type === responseType) {
        connection?.removeEventListener('message', listener);
        const pending = pendingRequests.get(id);

        if (pending) {
          clearTimeout(pending.timeout);
          pendingRequests.delete(id);

          // Log response if event type provided
          if (pending.logEventType && logger) {
            const basePayload: {
              request_id: string;
              success: boolean;
              error?: string;
              has_results?: boolean;
            } = {
              request_id: id,
              success: !!response.payload && !response.error,
              ...(response.error && { error: response.error }),
            };

            // Only add has_results for query-type operations
            const isQueryType =
              pending.logEventType === 'iot.web.event.query' ||
              pending.logEventType === 'iot.web.event.video' ||
              pending.logEventType === 'iot.web.activity.image';

            if (isQueryType && response.payload) {
              basePayload.has_results = !!response.payload;
            }

            logger.logAspenEvent(createLogEvent(pending.logEventType, 'response', basePayload));
          }

          if (response.error) {
            const error = new Error(response.error) as Error & { requestId?: string };
            error.requestId = id;
            reject(error);
          } else {
            resolve({ response: response.payload as TResponse, requestId: id });
          }
        }
      }
    };

    // Store pending request
    pendingRequests.set(id, {
      resolve: resolve as (value: unknown) => void,
      reject,
      timeout: timeoutHandle,
      requestType: type,
      responseType,
      startTime: Date.now(),
      logEventType,
    });

    // Log request if event type provided
    if (logEventType && logger) {
      logger.logAspenEvent(
        createLogEvent(logEventType, 'request', {
          request_id: id,
          query_type: type,
          query_payload: payload as object,
        }),
      );
    }

    if (connection) {
      connection.addEventListener('message', listener);

      try {
        connection.send(JSON.stringify(message));
      } catch (error) {
        pendingRequests.delete(id);
        clearTimeout(timeoutHandle);
        connection.removeEventListener('message', listener);
        const enhancedError = error as Error & { requestId?: string };
        enhancedError.requestId = id;
        reject(enhancedError);
      }
    }
  });
};

// Cleanup method for when connection is closed/reset
export const cleanup = (): void => {
  pendingRequests.forEach((pending) => {
    clearTimeout(pending.timeout);
    pending.reject(new Error('Connection closed'));
  });
  pendingRequests.clear();

  // Reset module state
  connection = null;
  logger = null;
};
